/**
 * What the workspace shows, persisted per device.
 *
 * Courses are hidden rather than deleted. He is on Matte 4 now and the other three are noise on
 * the way in — but the work already logged against them has to survive being switched off, and
 * be there unchanged when he starts Matte 5.
 */
import { computed, ref } from 'vue';

const KEY = 'euclid.settings';

export interface Settings {
  /**
   * Which courses the hub shows: a list of codes, or `'all'`.
   *
   * `'all'` is an explicit value rather than the empty list, because an empty list already means
   * something — the owner has switched every course off — and conflating the two meant unticking
   * the last course silently turned all of them back on.
   */
  visibleCourses: string[] | 'all';
  paper: 'system' | 'light' | 'dark';
}

const DEFAULTS: Settings = { visibleCourses: ['Ma4'], paper: 'system' };

/**
 * The paper theme's real home is its own key.
 *
 * `App.vue` reads `euclid.paper` before the first route renders and the canvas writes it when
 * the theme is cycled from a sheet, so that key — not this record — is the authority. This
 * record mirrors it so one settings surface can show every preference in one place.
 */
const PAPER_KEY = 'euclid.paper';

const storedPaper = (): Settings['paper'] | null => {
  try {
    const value = localStorage.getItem(PAPER_KEY);
    return value === 'light' || value === 'dark' || value === 'system' ? value : null;
  } catch {
    return null;
  }
};

const read = (): Settings => {
  const paper = storedPaper();
  try {
    const stored = localStorage.getItem(KEY);
    const base = stored ? { ...DEFAULTS, ...(JSON.parse(stored) as Partial<Settings>) } : DEFAULTS;
    return paper ? { ...base, paper } : base;
  } catch {
    return paper ? { ...DEFAULTS, paper } : DEFAULTS;
  }
};

const state = ref<Settings>(read());

export const settings = computed(() => state.value);

export function update(patch: Partial<Settings>): void {
  state.value = { ...state.value, ...patch };
  try { localStorage.setItem(KEY, JSON.stringify(state.value)); } catch { /* private window */ }
}

export const isVisible = (code: string): boolean =>
  state.value.visibleCourses === 'all' || state.value.visibleCourses.includes(code);

export function toggleCourse(code: string, all: string[] = []): void {
  const visible = state.value.visibleCourses;
  // Unticking one out of "all" has to name the rest explicitly, or the list it produces is a
  // list of one thing that happens to be missing.
  const list = visible === 'all' ? all : visible;
  update({
    visibleCourses: list.includes(code) ? list.filter(c => c !== code) : [...list, code],
  });
}

export const showingAll = computed(() => state.value.visibleCourses === 'all');
export const showingNone = computed(() =>
  state.value.visibleCourses !== 'all' && state.value.visibleCourses.length === 0);

/** The paper choices, in the order they are offered. */
export const PAPER_MODES: readonly Settings['paper'][] = ['system', 'light', 'dark'];

/**
 * Choose the paper, from anywhere.
 *
 * Writes both the record and `euclid.paper`, and applies it immediately, because the canvas
 * reads the key directly and `App.vue` reads it on boot. Setting only one of the two produced a
 * settings page that agreed with itself and disagreed with every sheet.
 */
export function applyPaper(mode: Settings['paper']): void {
  update({ paper: mode });
  try { localStorage.setItem(PAPER_KEY, mode); } catch { /* private window */ }
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (mode === 'system') delete root.dataset.theme;
  else root.dataset.theme = mode;
}

// ── The first-run choice ───────────────────────────────────────────────────────────────────

/**
 * Whether this device has ever answered the sign-in screen.
 *
 * It is a setting and it lives here beside the others, because that is all it is: one recorded
 * answer, read synchronously out of `localStorage`, on a screen that must paint with the network
 * off. Nothing in this section touches a server.
 */
const ENTRY_KEY = 'euclid.entry';

/** The key `supabase.ts` keeps a session under. Read, never written, and never parsed. */
const SESSION_KEY = 'euclid.auth';

export type EntryChoice = 'guest' | 'account';

export function entryChoice(): EntryChoice | null {
  try {
    const value = localStorage.getItem(ENTRY_KEY);
    return value === 'guest' || value === 'account' ? value : null;
  } catch {
    return null;
  }
}

export function rememberEntry(choice: EntryChoice): void {
  try { localStorage.setItem(ENTRY_KEY, choice); } catch { /* private window */ }
}

/** Test seam, and the only way back to a first visit. */
export function forgetEntry(): void {
  try { localStorage.removeItem(ENTRY_KEY); } catch { /* private window */ }
}

/**
 * A session already on the device, decided without building a client or asking anybody.
 *
 * The presence of the key is the whole question — somebody holding a session has plainly been
 * here before, and whether it is still valid is a matter for the server the first time it is
 * used, not for a route guard that must not wait on one.
 */
export const hasStoredSession = (): boolean => {
  try { return (localStorage.getItem(SESSION_KEY) ?? '').length > 0; } catch { return false; }
};

/**
 * The whole rule, as one expression, so it is tested without a browser.
 *
 * Two ways past it: you have already answered, or you are already signed in. Nothing else.
 *
 * It used to take a third — a device with any local work went straight through — and the effect
 * was that the owner was never asked at all, because his machine has had sheets on it since long
 * before the gate existed. Having drawn on a scratch sheet is not an answer to "do you want an
 * account"; it is only evidence that the question was never put. The owner:
 *
 *   *"Why am I not forced to log in / sign up when I enter the page … no log in = no profile =
 *   multiplayer hard"*
 *
 * So the question is asked once, on a device that has never answered it. Answering it — either
 * way, including "continue as guest" — is what makes it never appear again, and local work is
 * carried into an account untouched (`tests/auth.test.ts` fences that structurally: `auth.ts` may
 * not so much as import the storage layer).
 */
export const shouldGate = (
  chosen: EntryChoice | null,
  session: boolean,
): boolean => chosen === null && !session;

